import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Welcome.tsx — public marketing welcome page served at /welcome.
 *
 * Built from the approved InnovateFlow welcome artifact, recoloured to the
 * portal palette: iflow sky scale (#0c4a6e / #0369a1 / #0ea5e9 / #38bdf8) with
 * amber call-to-action buttons (#fbbf24, gray-900 text) — matching the rest of
 * the portal. New visitors arriving from marketing materials land here; the
 * existing landing page is unchanged and lives at /home.
 *
 * All styling is scoped under `.iflow-welcome` so the page-specific CSS
 * (section padding, .card, .btn, etc.) never leaks into the rest of the app.
 */

const STYLES = `
/* Self-hosted fonts (served from /fonts as 'self' — satisfies the portal CSP, no external CDN) */
@font-face{font-family:'Instrument Serif';font-style:normal;font-weight:400;font-display:swap;src:url('/fonts/instrument-serif-latin.woff2') format('woff2');}
@font-face{font-family:'Instrument Serif';font-style:italic;font-weight:400;font-display:swap;src:url('/fonts/instrument-serif-italic-latin.woff2') format('woff2');}
@font-face{font-family:'Space Mono';font-style:normal;font-weight:400;font-display:swap;src:url('/fonts/space-mono-latin.woff2') format('woff2');}
@font-face{font-family:'Space Mono';font-style:normal;font-weight:700;font-display:swap;src:url('/fonts/space-mono-700-latin.woff2') format('woff2');}
.iflow-welcome{
  --iflw-ink:#0C4A6E; --iflw-ink-2:#075985; --iflw-ink-3:#0369A1;
  --iflw-paper:#E8EFF4; --iflw-paper-2:#FFFFFF; --iflw-hair-light:rgba(12,74,110,.12);
  --iflw-accent:#38BDF8; --iflw-accent-deep:#0284C7;
  --iflw-cta:#FBBF24; --iflw-cta-hover:#FCD34D; --iflw-cta-ink:#111827;
  --iflw-text:#1E293B; --iflw-heading:#0C4A6E; --iflw-muted:#586577; --iflw-muted-dark:#BAE6FD;
  --iflw-hair-dark:rgba(255,255,255,.14);
  --iflw-hero-grad:linear-gradient(135deg,#0c4a6e 0%,#075985 50%,#0369a1 100%);
  --iflw-display:'Instrument Serif', Georgia, 'Times New Roman', serif;
  --iflw-body:'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  --iflw-mono:'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  --iflw-maxw:1120px;
  font-family:var(--iflw-body); color:var(--iflw-text); background:var(--iflw-paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; font-size:17px; min-height:100vh;
}
.iflow-welcome *{box-sizing:border-box;}
.iflow-welcome a{color:inherit; text-decoration:none;}
.iflow-welcome .wrap{max-width:var(--iflw-maxw); margin:0 auto; padding:0 28px;}
.iflow-welcome .eyebrow{
  font-family:var(--iflw-mono); font-size:12px; letter-spacing:.18em;
  text-transform:uppercase; color:var(--iflw-accent-deep); font-weight:700;
  display:inline-block; margin-bottom:18px;
}
.iflow-welcome .eyebrow.on-dark{color:var(--iflw-accent);}
.iflow-welcome h1,.iflow-welcome h2,.iflow-welcome h3{
  font-family:var(--iflw-display); font-weight:400; line-height:1.04;
  letter-spacing:-.01em; margin:0; color:var(--iflw-heading);
}
.iflow-welcome h2{font-size:clamp(30px,4.6vw,52px); margin-bottom:22px;}
.iflow-welcome h3{font-size:24px;}
.iflow-welcome p{margin:0 0 18px;}
.iflow-welcome .lead{font-size:19px; color:var(--iflw-muted);}

/* ---- top bar ---- */
.iflow-welcome header.bar{
  position:sticky; top:0; z-index:50;
  background:rgba(12,74,110,.86); backdrop-filter:blur(10px);
  border-bottom:1px solid var(--iflw-hair-dark);
}
.iflow-welcome .bar .wrap{display:flex; align-items:center; justify-content:space-between; height:64px;}
.iflow-welcome .brand{display:flex; align-items:center; gap:11px; color:#fff; font-weight:600; letter-spacing:.01em;}
.iflow-welcome .brand .glyph{width:28px; height:28px; flex:0 0 auto; border-radius:7px; object-fit:cover; display:block;}
.iflow-welcome nav.links{display:flex; gap:30px; align-items:center;}
.iflow-welcome nav.links a{color:#CDE3F5; font-size:14.5px; font-weight:500;}
.iflow-welcome nav.links a:hover{color:#fff;}
.iflow-welcome .btn{
  font-family:var(--iflw-body); font-weight:600; font-size:14.5px;
  border-radius:999px; padding:10px 20px; cursor:pointer; border:1px solid transparent;
  transition:transform .15s ease, background .2s ease, color .2s ease, border-color .2s ease;
  display:inline-block;
}
.iflow-welcome .btn-primary{background:var(--iflw-cta); color:var(--iflw-cta-ink);}
.iflow-welcome .btn-primary:hover{background:var(--iflw-cta-hover); transform:translateY(-1px);}
.iflow-welcome .btn-ghost{border-color:rgba(255,255,255,.32); color:#fff;}
.iflow-welcome .btn-ghost:hover{border-color:#fff;}
.iflow-welcome .bar .btn{padding:8px 18px;}
.iflow-welcome .nav-cta{display:inline-block;}

/* ---- hero ---- */
.iflow-welcome .hero{position:relative; background:var(--iflw-hero-grad); color:#fff; overflow:hidden;}
.iflow-welcome .hero .signature{
  position:absolute; right:-12%; top:50%; transform:translateY(-50%);
  width:min(820px,72vw); height:min(820px,72vw); opacity:.9; pointer-events:none;
}
.iflow-welcome .hero .signature path, .iflow-welcome .hero .signature ellipse{
  fill:none; stroke:#7DD3FC; stroke-width:1.1; vector-effect:non-scaling-stroke;
}
.iflow-welcome .hero .wrap{position:relative; z-index:2; padding:96px 28px 104px; max-width:920px; margin:0 auto;}
.iflow-welcome .hero h1{font-size:clamp(40px,6.4vw,78px); letter-spacing:-.015em; max-width:15ch; color:#fff;}
.iflow-welcome .hero h1 em{font-style:italic; color:var(--iflw-accent);}
.iflow-welcome .hero .lead{color:#DCEAF7; max-width:56ch; margin-top:24px; font-size:clamp(17px,2.1vw,21px);}
.iflow-welcome .hero .cta-row{display:flex; gap:14px; margin-top:38px; flex-wrap:wrap;}

/* ---- generic section ---- */
.iflow-welcome section{padding:96px 0;}
.iflow-welcome .section-paper{background:var(--iflw-paper);}
.iflow-welcome .section-white{background:var(--iflw-paper-2);}
.iflow-welcome .section-ink{background:var(--iflw-ink); color:#fff;}
.iflow-welcome .section-ink h2{color:#fff;}
.iflow-welcome .section-ink .lead, .iflow-welcome .section-ink p{color:var(--iflw-muted-dark);}
.iflow-welcome .two-col{display:grid; grid-template-columns:0.9fr 1.1fr; gap:64px; align-items:start;}
.iflow-welcome .measure{max-width:60ch;}

/* ---- how it works ---- */
.iflow-welcome .steps{display:grid; gap:26px; margin-top:14px;}
.iflow-welcome .step{
  display:grid; grid-template-columns:74px 1fr; gap:24px; align-items:start;
  padding:30px 0; border-top:1px solid var(--iflw-hair-dark);
}
.iflow-welcome .step:last-child{border-bottom:1px solid var(--iflw-hair-dark);}
.iflow-welcome .step .num{font-family:var(--iflw-mono); font-size:14px; color:var(--iflw-accent); padding-top:6px;}
.iflow-welcome .step h3{color:#fff; margin-bottom:8px;}
.iflow-welcome .step p{margin:0; color:var(--iflw-muted-dark);}

/* ---- audience cards ---- */
.iflow-welcome .cards{display:grid; grid-template-columns:repeat(3,1fr); gap:22px; margin-top:14px;}
.iflow-welcome .card{
  background:var(--iflw-paper-2); border:1px solid var(--iflw-hair-light); border-radius:16px;
  padding:30px 26px; transition:transform .18s ease, box-shadow .18s ease;
}
.iflow-welcome .card:hover{transform:translateY(-3px); box-shadow:0 16px 40px -24px rgba(12,74,110,.35);}
.iflow-welcome .card .k{font-family:var(--iflw-mono); font-size:12px; letter-spacing:.12em; text-transform:uppercase; color:var(--iflw-accent-deep); display:block; margin-bottom:14px;}
.iflow-welcome .card h3{font-size:21px; margin-bottom:10px;}
.iflow-welcome .card p{color:var(--iflw-muted); margin:0; font-size:15.5px;}

/* ---- trust grid ---- */
.iflow-welcome .trust-grid{display:grid; grid-template-columns:repeat(2,1fr); gap:30px 48px; margin-top:10px;}
.iflow-welcome .trust-item{border-top:1px solid var(--iflw-hair-dark); padding-top:20px;}
.iflow-welcome .trust-item h3{font-size:20px; color:#fff; margin-bottom:8px;}
.iflow-welcome .trust-item p{color:var(--iflw-muted-dark); margin:0; font-size:15.5px;}
.iflow-welcome .honest-note{
  margin-top:54px; padding:26px 30px; border-left:2px solid var(--iflw-accent);
  background:var(--iflw-ink-2); border-radius:0 12px 12px 0; max-width:62ch;
}
.iflow-welcome .honest-note p{margin:0; color:#EAF3FB; font-style:italic; font-family:var(--iflw-display); font-size:21px; line-height:1.4;}

/* ---- leadership ---- */
.iflow-welcome .team{display:grid; grid-template-columns:repeat(2,1fr); gap:22px; margin-top:18px; max-width:760px;}
.iflow-welcome .person{padding:26px; border:1px solid var(--iflw-hair-light); border-radius:16px; background:var(--iflw-paper-2);}
.iflow-welcome .person .ini{
  width:46px; height:46px; border-radius:50%; display:grid; place-items:center;
  font-family:var(--iflw-mono); font-weight:700; font-size:15px; color:#0C4A6E;
  background:var(--iflw-accent); margin-bottom:16px;
}
.iflow-welcome .person .role{font-family:var(--iflw-mono); font-size:12px; letter-spacing:.08em; text-transform:uppercase; color:var(--iflw-accent-deep); margin-bottom:6px;}
.iflow-welcome .person .name{font-family:var(--iflw-display); font-size:23px; margin-bottom:8px; color:var(--iflw-heading);}
.iflow-welcome .person p{color:var(--iflw-muted); font-size:15px; margin:0;}

/* ---- contact ---- */
.iflow-welcome .contact-card{
  background:var(--iflw-ink-2); border:1px solid var(--iflw-hair-dark); border-radius:22px;
  padding:54px 48px; display:grid; grid-template-columns:1.1fr .9fr; gap:48px; align-items:center;
}
.iflow-welcome .contact-card h2{color:#fff; margin-bottom:14px;}
.iflow-welcome .contact-card p{color:var(--iflw-muted-dark);}
.iflow-welcome .contact-detail{display:flex; flex-direction:column; gap:20px;}
.iflow-welcome .detail{border-top:1px solid var(--iflw-hair-dark); padding-top:16px;}
.iflow-welcome .detail .l{font-family:var(--iflw-mono); font-size:11.5px; letter-spacing:.12em; text-transform:uppercase; color:var(--iflw-muted-dark); display:block; margin-bottom:6px;}
.iflow-welcome .detail .v{color:#fff; font-size:18px;}
.iflow-welcome .detail .v a:hover{color:var(--iflw-accent);}

/* ---- footer ---- */
.iflow-welcome footer.foot{background:var(--iflw-ink); color:var(--iflw-muted-dark); padding:46px 0; border-top:1px solid var(--iflw-hair-dark);}
.iflow-welcome footer.foot .wrap{display:flex; justify-content:space-between; align-items:center; gap:20px; flex-wrap:wrap;}
.iflow-welcome footer.foot .brand{color:#fff;}
.iflow-welcome .foot-tag{font-size:13.5px; max-width:42ch;}
.iflow-welcome .foot-meta{font-family:var(--iflw-mono); font-size:12px; letter-spacing:.04em;}

/* ---- reveal ---- */
.iflow-welcome .reveal{opacity:0; transform:translateY(18px); transition:opacity .7s ease, transform .7s ease;}
.iflow-welcome .reveal.in{opacity:1; transform:none;}

/* ---- responsive ---- */
@media (max-width:860px){
  .iflow-welcome nav.links{display:none;}
  .iflow-welcome .two-col{grid-template-columns:1fr; gap:34px;}
  .iflow-welcome .cards,.iflow-welcome .team{grid-template-columns:1fr;}
  .iflow-welcome .trust-grid{grid-template-columns:1fr;}
  .iflow-welcome .contact-card{grid-template-columns:1fr; padding:38px 28px; gap:30px;}
  .iflow-welcome .hero .signature{opacity:.5; right:-30%;}
  .iflow-welcome section{padding:72px 0;}
  .iflow-welcome .hero .wrap{padding:72px 24px 80px;}
}
@media (prefers-reduced-motion:reduce){
  .iflow-welcome .reveal{opacity:1; transform:none; transition:none;}
  .iflow-welcome .sig-draw{animation:none !important; stroke-dashoffset:0 !important;}
}
.iflow-welcome :focus-visible{outline:2px solid var(--iflw-accent); outline-offset:3px; border-radius:4px;}

/* signature draw animation */
.iflow-welcome .sig-draw{stroke-dasharray:2600; stroke-dashoffset:2600; animation:iflw-draw 2.6s ease forwards;}
.iflow-welcome .sig-draw:nth-child(2){animation-delay:.12s;}
.iflow-welcome .sig-draw:nth-child(3){animation-delay:.24s;}
.iflow-welcome .sig-draw:nth-child(4){animation-delay:.36s;}
.iflow-welcome .sig-draw:nth-child(5){animation-delay:.48s;}
.iflow-welcome .sig-draw:nth-child(6){animation-delay:.6s;}
.iflow-welcome .sig-draw:nth-child(7){animation-delay:.72s;}
.iflow-welcome .sig-draw:nth-child(8){animation-delay:.84s;}
.iflow-welcome .sig-draw:nth-child(9){animation-delay:.96s;}
@keyframes iflw-draw{to{stroke-dashoffset:0;}}
`

const Glyph = () => (
  <img className="glyph" src="/logo.png" alt="InnovateFlow logo" />
)

export default function Welcome() {
  const navigate = useNavigate()

  // Enable smooth in-page scrolling for the anchor nav while this page is mounted.
  // (Display/mono fonts are self-hosted via @font-face in STYLES — no external load.)
  useEffect(() => {
    const prevScroll = document.documentElement.style.scrollBehavior
    document.documentElement.style.scrollBehavior = 'smooth'
    return () => {
      document.documentElement.style.scrollBehavior = prevScroll
    }
  }, [])

  // Reveal-on-scroll for sections.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.iflow-welcome .reveal'))
    if (!('IntersectionObserver' in window)) {
      els.forEach((e) => e.classList.add('in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in')
            io.unobserve(en.target)
          }
        })
      },
      { threshold: 0.14 }
    )
    els.forEach((e) => io.observe(e))
    return () => io.disconnect()
  }, [])

  return (
    <div className="iflow-welcome">
      <style>{STYLES}</style>

      {/* ============ TOP BAR ============ */}
      <header className="bar">
        <div className="wrap">
          <a className="brand" href="#top" aria-label="InnovateFlow home">
            <Glyph />
            InnovateFlow
          </a>
          <nav className="links" aria-label="Primary">
            <a href="#what">What it is</a>
            <a href="#how">How it works</a>
            <a href="#trust">Trust</a>
            <a href="#contact">Contact</a>
            <a className="nav-signin" onClick={() => navigate('/login')} style={{ cursor: 'pointer' }}>Sign in</a>
            <a className="btn btn-primary nav-cta" href="#contact">Request access</a>
          </nav>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="hero" id="top">
        <svg className="signature" viewBox="0 0 600 600" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
          <g transform="translate(300 300)">
            <ellipse className="sig-draw" rx="40" ry="52" transform="rotate(-12)" />
            <ellipse className="sig-draw" rx="78" ry="96" transform="rotate(-12)" />
            <ellipse className="sig-draw" rx="118" ry="142" transform="rotate(-11)" />
            <path className="sig-draw" d="M-150 -150 A 170 196 -10 0 1 158 -120" />
            <ellipse className="sig-draw" rx="160" ry="190" transform="rotate(-10)" />
            <path className="sig-draw" d="M-205 70 A 210 236 -8 0 0 150 215" />
            <ellipse className="sig-draw" rx="208" ry="242" transform="rotate(-9)" />
            <ellipse className="sig-draw" rx="258" ry="296" transform="rotate(-8)" />
            <path className="sig-draw" d="M-300 -70 A 300 330 -6 0 1 -120 -290" />
          </g>
        </svg>
        <div className="wrap">
          <span className="eyebrow on-dark">Trust infrastructure for the innovation economy</span>
          <h1>
            Find who needs your invention — <em>before</em> you reveal it.
          </h1>
          <p className="lead">
            InnovateFlow helps inventors and organisations discover the right partners across industries while their
            documents stay private. Your work is matched on a{' '}
            <strong style={{ color: '#fff', fontWeight: 600 }}>signature of the idea</strong> — not the idea itself —
            and you decide what to disclose, step by step.
          </p>
          <div className="cta-row">
            <a className="btn btn-primary" href="#contact">Request access</a>
            <a className="btn btn-ghost" href="#how">See how it works</a>
          </div>
        </div>
      </section>

      {/* ============ WHY WE EXIST ============ */}
      <section className="section-paper" id="what">
        <div className="wrap two-col reveal">
          <div>
            <span className="eyebrow">Why we exist</span>
            <h2>The best ideas stall on a paradox.</h2>
          </div>
          <div className="measure">
            <p>
              To learn who values your innovation, you have to describe it. But describing it — before it is protected —
              is how an idea gets lost. So the most valuable conversations never happen, and good work sits idle.
            </p>
            <p>
              InnovateFlow removes that bind. We are building the trust layer for the coordination economy: a place where
              two parties can find each other and explore a fit without either side over-exposing what makes them
              valuable.
            </p>
            <p>
              We began in innovation, where the tension is sharpest — but the same architecture holds anywhere
              disclosure, trust and privacy collide.
            </p>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="section-ink" id="how">
        <div className="wrap reveal">
          <span className="eyebrow on-dark">How it works</span>
          <h2>Three moves, in your control.</h2>
          <div className="steps">
            <div className="step">
              <div className="num">01 / Analyse</div>
              <div>
                <h3>Understand your idea, privately</h3>
                <p>
                  Upload your disclosure to a private workspace. InnovateFlow returns a plain-English summary, novelty
                  and prior-art signals, merit scores and concrete next steps — for your eyes first.
                </p>
              </div>
            </div>
            <div className="step">
              <div className="num">02 / Match</div>
              <div>
                <h3>Matched on a signature, not the document</h3>
                <p>
                  Your work is reduced to a non-reversible signature and matched across the wider network through a
                  separated architecture. Relevant counterparts surface as scored connections — without your documents
                  being shared with them.
                </p>
              </div>
            </div>
            <div className="step">
              <div className="num">03 / Disclose</div>
              <div>
                <h3>Reveal on your terms — the disclosure ladder</h3>
                <p>
                  When a match is worth pursuing, the two sides reveal information to each other rung by rung, and only by
                  explicit consent. Each commitment is cryptographically signed, so every step is on the record.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHO IT'S FOR ============ */}
      <section className="section-white" id="who">
        <div className="wrap reveal">
          <span className="eyebrow">Who it's for</span>
          <h2>One architecture, several worlds.</h2>
          <div className="cards">
            <div className="card">
              <span className="k">Inventors &amp; founders</span>
              <h3>Test the market before you file</h3>
              <p>
                Find out who wants your idea across industries you would never think to search — without giving it away
                in the process.
              </p>
            </div>
            <div className="card">
              <span className="k">Innovation &amp; R&amp;D teams</span>
              <h3>Surface partners and licensees</h3>
              <p>
                Turn a portfolio of ideas into a map of the people, organisations and capital that could move them
                forward.
              </p>
            </div>
            <div className="card">
              <span className="k">Aggregators</span>
              <h3>Run a whole portfolio, safely</h3>
              <p>
                Universities, accelerators and IP managers can manage many inventors under one roof, with controlled,
                role-based access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST & SECURITY ============ */}
      <section className="section-ink" id="trust">
        <div className="wrap reveal">
          <span className="eyebrow on-dark">Built for trust</span>
          <h2>Security you can reason about.</h2>
          <div className="trust-grid">
            <div className="trust-item">
              <h3>Matched, not exposed</h3>
              <p>
                Discovery runs on a signature of your work through a separated architecture. Other parties don't receive
                your documents in order to make a match.
              </p>
            </div>
            <div className="trust-item">
              <h3>Your keys, your control</h3>
              <p>
                Bring your own encryption key, so your organisation holds the keys to its own data rather than handing
                that control to us.
              </p>
            </div>
            <div className="trust-item">
              <h3>Signed commitments</h3>
              <p>
                Every step on the disclosure ladder is cryptographically signed, creating a clear, consented record of
                who shared what, and when.
              </p>
            </div>
            <div className="trust-item">
              <h3>Australian by default</h3>
              <p>Hosted in Australia, in the Sydney region, with an independent IRAP security assessment underway.</p>
            </div>
          </div>
          <div className="honest-note">
            <p>
              We describe what the platform does in plain, checkable terms — and nothing more. Trust is earned by
              realism, not absolutes.
            </p>
          </div>
        </div>
      </section>

      {/* ============ LEADERSHIP ============ */}
      <section className="section-paper" id="team">
        <div className="wrap reveal">
          <span className="eyebrow">Who's behind it</span>
          <h2>Built in Sydney, by people who live where trust matters.</h2>
          <div className="team">
            <div className="person">
              <div className="ini">RP</div>
              <div className="role">Founder &amp; CEO</div>
              <div className="name">Roger Price</div>
              <p>
                An intensive-care paramedic for 27 years, now a physical-products inventor across medical devices,
                mobility and multi-tools. He built InnovateFlow to solve a problem he kept running into himself.
              </p>
            </div>
            <div className="person">
              <div className="ini">RF</div>
              <div className="role">Chief Technology Officer</div>
              <div className="name">Rob Francis</div>
              <p>
                Leads the platform and the engineering behind InnovateFlow's matching, disclosure and security
                architecture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section className="section-ink" id="contact">
        <div className="wrap reveal">
          <div className="contact-card">
            <div>
              <span className="eyebrow on-dark">Get in touch</span>
              <h2>Let's talk about what you're protecting.</h2>
              <p>
                Whether you're an inventor, an innovation team, or an organisation with a portfolio to manage, we'd like
                to hear from you. Tell us what you're working on and we'll arrange access.
              </p>
              <a className="btn btn-primary" href="mailto:hello@innovateflow.com.au" style={{ marginTop: 8 }}>
                Request access
              </a>
            </div>
            <div className="contact-detail">
              <div className="detail">
                <span className="l">Email</span>
                <span className="v">
                  <a href="mailto:hello@innovateflow.com.au">hello@innovateflow.com.au</a>
                </span>
              </div>
              <div className="detail">
                <span className="l">Based in</span>
                <span className="v">Sydney, Australia</span>
              </div>
              <div className="detail">
                <span className="l">Company</span>
                <span className="v">InnovateFlow Pty Ltd</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="foot">
        <div className="wrap">
          <div>
            <div className="brand" style={{ marginBottom: 8 }}>
              <Glyph />
              InnovateFlow
            </div>
            <div className="foot-tag">Trust and privacy infrastructure for the coordination economy.</div>
          </div>
          <div className="foot-meta">© 2026 InnovateFlow Pty Ltd · Sydney, Australia</div>
        </div>
      </footer>
    </div>
  )
}
